import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Directionality } from '@angular/cdk/bidi';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { APP_ROUTE } from './app.routes';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
// import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { ErrorInterceptor } from '@core/interceptor/error.interceptor';
import { DirectionService, LanguageService, AppDirectionality } from '@core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPermissionsModule } from 'ngx-permissions';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { FeatherModule } from 'angular-feather';
import { allIcons } from 'angular-feather/icons';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { appInitializerProviders } from '@core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { environment } from '../environments/environment';
import { AppConfigService } from './AppConfigService.service';
import { CenterService } from './centerService.service';
import { firstValueFrom } from 'rxjs';
import { I18nMatPaginatorIntl } from '@core/service/mat-paginator-intl.service';
import { ChunkLoadRecoveryService } from '@core/service/chunk-load-recovery.service';
import { GlobalErrorHandlerService } from '@core/service/global-error-handler.service';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

export function initApp(
  appConfig: AppConfigService,
  centerService: CenterService,
  languageService: LanguageService
) {
  return async () => {
    const url = new URL(window.location.href);
    const queryOverride = url.searchParams.get('centerHost')?.trim() || '';
    const storageOverride = localStorage.getItem('centerHostOverride')?.trim() || '';
    const domain = queryOverride || storageOverride || window.location.hostname || '';

    try {
      const centerConfig = await firstValueFrom(
        centerService.getCenterConfigByDomain(domain)
      );
      appConfig.setCenter(centerConfig.center);
      appConfig.setCenterConfig(centerConfig);
      languageService.initLanguage(centerConfig);
    } catch (error) {
      console.error('Error loading center config:', error);
      const fallbackConfig = {
        center: 'default',
        domain,
        defaultLang: 'es',
        langs: ['es'],
        homeImage: 'assets/images/pages/home.png',
        backgroundImage: 'assets/images/fondo.jpg',
        loadingImage: 'assets/images/logo.png',
        logo: 'assets/images/logo.png',
        name: 'Tdsystem',
        companyLogo: 'assets/images/logo.png',
        companyName: 'Tdsystem',
      };

      appConfig.setCenter('default');
      appConfig.setCenterConfig(fallbackConfig);
      languageService.initLanguage(fallbackConfig);
    }
  };
}

export function initChunkLoadRecovery(chunkLoadRecoveryService: ChunkLoadRecoveryService) {
  return () => chunkLoadRecoveryService.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    CenterService,
    LanguageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(APP_ROUTE),
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    // { provide: LocationStrategy, useClass: HashLocationStrategy },
    DirectionService,
    LanguageService,
    { provide: Directionality, useClass: AppDirectionality },
    provideTranslateService({
      fallbackLang: 'es',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        // Los assets de traducción no llevan hash en el nombre. Este valor evita
        // que un despliegue nuevo reutilice un JSON anterior desde el navegador/CDN.
        suffix: `.json?v=${Date.now()}`,
      }),
    }),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: MomentDateAdapter },
    appInitializerProviders,
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'YYYY-MM-DD',
        },
        display: {
          dateInput: 'YYYY-MM-DD',
          monthYearLabel: 'YYYY MMM',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY MMM',
        },
      },
    },
    importProvidersFrom(FeatherModule.pick(allIcons)),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideCharts(withDefaultRegisterables()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    {
      provide: MatPaginatorIntl,
      useClass: I18nMatPaginatorIntl,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [AppConfigService, CenterService, LanguageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initChunkLoadRecovery,
      deps: [ChunkLoadRecoveryService],
      multi: true,
    },
    provideAnimationsAsync(),
  ]
};
